import { useLanguage } from "@/contexts/LanguageContext";
import { SEO } from "@/components/SEO";
import { Shield, Lock, Eye, Share2 } from "lucide-react";

import { PageHero } from "@/components/shared/PageHero";
import { GlowCard } from "@/components/shared/GlowCard";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const { ref } = useRevealOnScroll<HTMLDivElement>();

  const sections =
    language === "ar"
      ? [
          { title: "جمع المعلومات", content: "نحن نجمع المعلومات التي تقدمها لنا عند إنشاء حملة إعلانية. تشمل هذه المعلومات اسمك، عنوان بريدك الإلكتروني، رقم هاتفك، واسم شركتك.", icon: Eye, accent: "blue" as const },
          { title: "استخدام المعلومات", content: "نستخدم المعلومات التي نجمعها لتوفير وتحسين خدماتنا، ولإدارة حملاتك الإعلانية، وللتواصل معك بشأن خدماتنا.", icon: Lock, accent: "violet" as const },
          { title: "حماية المعلومات", content: "نحن نتخذ إجراءات أمنية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو التغيير أو الإفصاح أو التدمير.", icon: Shield, accent: "emerald" as const },
          { title: "مشاركة المعلومات", content: "لا نبيع أو نؤجر أو نتاجر بمعلوماتك الشخصية مع أطراف ثالثة. قد نشارك معلوماتك مع مقدمي الخدمات الذين يساعدوننا في تشغيل موقعنا وإدارة أعمالنا.", icon: Share2, accent: "amber" as const },
        ]
      : [
          { title: "Information Collection", content: "We collect information you provide to us when creating an advertising campaign. This includes your name, email address, phone number, and company name.", icon: Eye, accent: "blue" as const },
          { title: "Use of Information", content: "We use the information we collect to provide and improve our services, manage your advertising campaigns, and communicate with you about our services.", icon: Lock, accent: "violet" as const },
          { title: "Information Protection", content: "We take appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.", icon: Shield, accent: "emerald" as const },
          { title: "Information Sharing", content: "We do not sell, rent, or trade your personal information with third parties. We may share your information with service providers who help us operate our website and run our business.", icon: Share2, accent: "amber" as const },
        ];

  return (
    <div className="relative min-h-screen">
      <SEO
        title="Privacy Policy — Fusion Media"
        description="How Fusion Media collects, uses, and protects your personal information across our advertising platform."
      />

      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHero
          eyebrow="Privacy & Security"
          title={language === "ar" ? "سياسة" : "Privacy"}
          accent={language === "ar" ? "الخصوصية" : "Policy"}
          subtitle={
            language === "ar"
              ? "نحن نقدر خصوصيتك ونلتزم بحماية بياناتك الشخصية وضمان أعلى معايير الأمان"
              : "We value your privacy and are committed to protecting your personal data with the highest security standards."
          }
          centered
        />

        <section ref={ref} className="reveal-on-scroll pb-16 md:pb-24">
          <div className="max-w-4xl mx-auto grid gap-5">
            {sections.map((s, i) => (
              <GlowCard key={i} icon={s.icon} accent={s.accent} title={s.title} description={s.content} />
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center mt-10">
            {language === "ar" ? "آخر تحديث: فبراير ٢٠٢٥" : "Last updated: February 2025"}
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
